/**
 * Client-side mirror of every modular ship and debris body the server has told
 * us about. Structure comes from shipSync (rebuilt locally with the shared
 * rebuild(), from the exact HP the server rebuilt from), part HP from shipHp.
 */

import {
  Blueprint, Catalog, PlacedGeom, ShipState, ShipTuning,
  ServerShipSyncMessage, ServerShipHpMessage, ServerShipHpBatchMessage,
  blueprintGeometry, createShipState, rebuildShip,
} from '@space-game/common';

/** What the ship renderer, HUD and hangar read from. */
export interface ShipSource {
  readonly catalog: Catalog;
  readonly tuning: ShipTuning;
  readonly bodies: Map<number, ShipState>;
  get(id: number): ShipState | undefined;
  geometry(ship: ShipState): PlacedGeom[];
}

export class ShipStore implements ShipSource {
  readonly bodies = new Map<number, ShipState>();
  private geomCache = new WeakMap<Blueprint, PlacedGeom[]>();
  private lastSeen = new Map<number, number>();

  /** A sync came without a blueprint for a ship we don't have: ask for the whole thing */
  onMissing: ((id: number) => void) | null = null;
  /** Designs by hash: the server sends another ship of a design we have by its hash alone */
  private designs = new Map<string, Blueprint>();

  constructor(readonly catalog: Catalog, readonly tuning: ShipTuning) {}

  get(id: number): ShipState | undefined {
    return this.bodies.get(id);
  }

  geometry(ship: ShipState): PlacedGeom[] {
    let g = this.geomCache.get(ship.blueprint);
    if (!g) {
      g = blueprintGeometry(this.catalog, ship.blueprint);
      this.geomCache.set(ship.blueprint, g);
    }
    return g;
  }

  /** Returns the parts that came back since the last sync (station repair) */
  onSync(msg: ServerShipSyncMessage): number[] {
    if (msg.blueprint && msg.design) {
      this.designs.delete(msg.design);
      this.designs.set(msg.design, msg.blueprint);
      if (this.designs.size > 300) this.designs.delete(this.designs.keys().next().value!);
    } else if (msg.design) {
      // A design we were sent before
      const bp = this.designs.get(msg.design);
      if (!bp) { this.onMissing?.(msg.id); return []; }
      msg = { ...msg, blueprint: bp };
    }
    const prev = this.bodies.get(msg.id);
    // Without a blueprint it's the ship we have, rebuilt; with one it may be a new design
    if (!msg.blueprint && !prev) { this.onMissing?.(msg.id); return []; }
    const sameShape = !!prev && (!msg.blueprint || sameBlueprint(prev.blueprint, msg.blueprint));
    const wasAlive = sameShape && prev && prev.kind === 'ship' ? [...prev.derived.alive] : null;
    const ship = sameShape
      ? prev!
      : createShipState(this.catalog, this.tuning, msg.id, msg.blueprint!, 0, { kind: msg.kind });
    ship.kind = msg.kind;
    if (msg.hp) ship.hp = [...msg.hp];
    // (a boss's hull isn't the blueprint's: its true maximums, so full health draws as full)
    if (msg.maxHp && msg.maxHp.length === ship.maxHp.length) ship.maxHp = [...msg.maxHp];
    else for (const [i, h] of msg.hpd ?? []) ship.hp[i] = h;
    ship.detached = ship.blueprint.parts.map((_, i) => msg.detached.includes(i));
    ship.ghost = ship.blueprint.parts.map((_, i) => !!msg.ghosts?.includes(i));
    rebuildShip(ship, this.catalog, this.tuning);
    ship.rebuilds = msg.rebuilds;
    if (sameShape && prev) ship.angVel = prev.angVel;
    this.bodies.set(msg.id, ship);
    return wasAlive && ship.kind === 'ship' ? ship.derived.alive.map((a, i) => (a && !wasAlive[i] ? i : -1)).filter((i) => i >= 0) : [];
  }

  onHp(msg: ServerShipHpMessage): void {
    const ship = this.bodies.get(msg.id);
    if (!ship) return;
    for (const [i, hp] of msg.hp) ship.hp[i] = hp;
  }

  onHps(msg: ServerShipHpBatchMessage): void {
    for (const [id, hp] of msg.ships) {
      const ship = this.bodies.get(id);
      if (ship) for (const [i, h] of hp) ship.hp[i] = h;
    }
  }

  /**
   * Forget bodies whose entity the client hasn't seen for `graceMs` (a sync can
   * arrive a moment before the snapshot that introduces its entity).
   */
  prune(alive: (id: number) => boolean, now: number, graceMs = 2000): void {
    for (const id of this.bodies.keys()) {
      if (alive(id)) this.lastSeen.set(id, now);
      else if (now - (this.lastSeen.get(id) ?? now) > graceMs) { this.bodies.delete(id); this.lastSeen.delete(id); }
      else if (!this.lastSeen.has(id)) this.lastSeen.set(id, now);
    }
  }

  remove(id: number): void {
    this.bodies.delete(id);
    this.lastSeen.delete(id);
  }
}

/** Same design (parts in the same places), without serialising both */
function sameBlueprint(a: Blueprint, b: Blueprint): boolean {
  if (a === b) return true;
  if (a.parts.length !== b.parts.length || a.name !== b.name || a.paint !== b.paint) return false;
  for (let i = 0; i < a.parts.length; i++) {
    const p = a.parts[i], q = b.parts[i];
    if (p.uid !== q.uid || p.fn !== q.fn || p.piece !== q.piece || p.material !== q.material || p.x !== q.x || p.y !== q.y || p.rot !== q.rot
      || p.flip !== q.flip || p.mount !== q.mount || p.facing !== q.facing) return false;
  }
  return true;
}
